import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export interface MultidoorState {
    id: number;
    name: string;
    slug: string;
    apiUrl: string;
    frontendUrl: string | null;
    deploymentType: 'central' | 'self-hosted';
    isLaunched: boolean;
}

interface StateContextValue {
    states: MultidoorState[];
    loading: boolean;
    selectedState: MultidoorState | null;
    selectStateBySlug: (slug: string) => void;
}

const StateContext = createContext<StateContextValue | null>(null);

const PODUS_API_URL = import.meta.env.VITE_PODUS_API_URL ?? 'http://localhost:9000';
const PODUS_SYSTEM_KEY = 'G@V3l2023-new-podus-v001233-SyS';
const SELECTED_SLUG_KEY = 'md_selected_state_slug';
const API_BASE_KEY = 'md_api_base';

/** Accept PODUS_API_URL configured with or without a trailing /api segment. */
function statesEndpoint(): string {
    const trimmed = PODUS_API_URL.replace(/\/$/, '');
    const withApi = trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
    return `${withApi}/multidoor/states`;
}

export function StateProvider({ children }: { children: ReactNode }) {
    const [states, setStates] = useState<MultidoorState[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedSlug, setSelectedSlug] = useState<string | null>(() => localStorage.getItem(SELECTED_SLUG_KEY));

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(statesEndpoint(), {
                    headers: { Authorization: `Bearer ${PODUS_SYSTEM_KEY}` },
                });
                const json = await res.json();
                const list: MultidoorState[] = json?.response?.data ?? [];
                console.log('Fetched states:', list);
                if (cancelled) return;
                setStates(list);
                // Auto-select the first state if nothing was persisted
                if (!selectedSlug && list.length > 0) {
                    setSelectedSlug(list[0].slug);
                    localStorage.setItem(SELECTED_SLUG_KEY, list[0].slug);
                    localStorage.setItem(API_BASE_KEY, list[0].apiUrl);
                }
            } catch {
                if (!cancelled) setStates([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const selectedState = useMemo(() => states.find((s) => s.slug === selectedSlug) ?? null, [states, selectedSlug]);

    // Keep api base in sync whenever selection resolves to a concrete state
    useEffect(() => {
        if (selectedState) {
            localStorage.setItem(API_BASE_KEY, selectedState.apiUrl);
        }
    }, [selectedState]);

    const selectStateBySlug = useCallback((slug: string) => {
        setSelectedSlug(slug);
        localStorage.setItem(SELECTED_SLUG_KEY, slug);
    }, []);

    const value = useMemo<StateContextValue>(
        () => ({ states, loading, selectedState, selectStateBySlug }),
        [states, loading, selectedState, selectStateBySlug],
    );

    return <StateContext.Provider value={value}>{children}</StateContext.Provider>;
}

export function useMultidoorState(): StateContextValue {
    const ctx = useContext(StateContext);
    if (!ctx) throw new Error('useMultidoorState must be used within StateProvider');
    return ctx;
}
